import React, { useEffect, useRef, useState } from "react";
import styles from "styles.module.css";
import { useTranslation } from "react-i18next";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import {
	getRegistrationOverviewApi,
	RegistrationOverviewParams,
} from "api/registration";
import { registrationOverviewGridColumns } from "./utils/OverviewGridColumns";
import OverviewFilter from "./utils/OverviewFilter";
import { useLocation, useNavigate } from "react-router";
import { useMenuItemStore } from "store/menuItem";
import { findMenuItemByPath } from "utils/helper";
import { getPageAccessApi } from "api/menu";
import { PageAccessByMenu } from "interface/user";
import { PageTypePath } from "utils/pages";
import RegistrationOverviewMobileList from "./RegistrationMobileList";
import { RegistrationOverview } from "interface/registration";

const RegistrationOverviewPage: React.FC = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const { menuItems } = useMenuItemStore();
	const currentPath = location.pathname;

	// const [pageAccess, setPageAccess] = useState<PageAccessByMenu | null>(null);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1600);
	const [mobileData, setMobileData] = useState<RegistrationOverview[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const [filterOptions, setFilterOptions] =
		useState<RegistrationOverviewParams>({
			registrationTimeFrom: new Date(),
			registrationTimeTo: new Date(),
			landingTimeFrom: null,
			landingTimeTo: null,
		});

	// useEffect(() => {
	// 	const pageHandler = async () => {
	// 		const menu = findMenuItemByPath(menuItems, currentPath);
	// 		try {
	// 			if (!pageAccess && menu) {
	// 				const access = await getPageAccessApi(menu.id);
	// 				setPageAccess(access);
	// 			}
	// 		} catch (error) {
	// 			console.error("Error fetching page access:", error);
	// 		} finally {
	// 		}
	// 	};

	// 	pageHandler();

	// 	return () => {};
	// }, [currentPath, menuItems]);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 1600);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		setMobileLoading(true);
		getRegistrationOverviewApi(filterOptions)
			.then((res) => {
				setMobileData(res.data ?? []);
			})
			.finally(() => {
				setMobileLoading(false);
			});
	}, [isMobileView, filterOptions]);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const handleLandingNote = (props: any) => {
		window.open(
			`/${PageTypePath.FisheryDepartmentLandingNotes}/${props.registrationId}`,
		);
	};

	const handleSalesNote = (props: any) => {
		window.open(
			`/${PageTypePath.FisheryDepartmentSalesNotes}/${props.registrationId}`,
		);
	};

	return (
		<div className={styles.PageContainer}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("RegistrationOverview")}
				</legend>
			</div>
			<div>
				<OverviewFilter
					filterOptions={filterOptions}
					setFilterOptions={setFilterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
				/>
			</div>
			<div style={{ marginTop: 30 }}>
				{isMobileView ? (
					<RegistrationOverviewMobileList
						items={mobileData}
						loading={mobileLoading}
						onViewLandingNote={handleLandingNote}
						onViewSalesNote={handleSalesNote}
						onLoadMore={() => undefined}
					/>
				) : (
					<GridWithGroupPaging
						gridColumns={registrationOverviewGridColumns({
							onViewLandingNote: handleLandingNote,
							onViewSalesNote: handleSalesNote,
						})}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						apiMethod={(params) =>
							getRegistrationOverviewApi(params).then((res) => res.data ?? [])
						}
						filter={filterOptions}
						itemAboveHeight={100}
						pageable={false}
					/>
				)}
			</div>
		</div>
	);
};

export default RegistrationOverviewPage;
